/**
 * itemConstants.js
 * 
 * Shared constants for projects and collections (items).
 * This file centralizes all status types, visibility types, and other
 * common constants to ensure consistency across the application.
 * 
 * Note: "Item" refers to projects and collections, NOT map entities (pins, polygons, etc.)
 */

// ============================================================================
// STATUS CONSTANTS
// ============================================================================

/**
 * Status options for projects
 * These define the lifecycle states a project can be in
 */
export const PROJECT_STATUSES = {
  DRAFT: "DRAFT",                    // Initial state when project is created
  LIVE: "LIVE",                      // Active project that's being used
  ARCHIVED: "ARCHIVED",              // Project that's been archived but not deleted
  PENDING_APPROVAL: "PENDING_APPROVAL", // Waiting for approval (if approval workflow exists)
  APPROVED: "APPROVED",              // Approved project
  REJECTED: "REJECTED",              // Rejected project
  DELETED: "DELETED"                 // Soft-deleted project (hidden from UI)
};

/**
 * Status options for collections
 * These define the lifecycle states a collection can be in
 */
export const COLLECTION_STATUSES = {
  DRAFT: "DRAFT",                    // Initial state when collection is created
  LIVE: "LIVE",                      // Active collection that's being used
  ARCHIVED: "ARCHIVED",              // Collection that's been archived but not deleted
  PENDING_APPROVAL: "PENDING_APPROVAL", // Waiting for approval (if approval workflow exists)
  APPROVED: "APPROVED",              // Approved collection
  REJECTED: "REJECTED",              // Rejected collection
  DELETED: "DELETED"                 // Soft-deleted collection (hidden from UI)
};

/**
 * Shared status options that apply to both projects and collections
 * Use this when you need a unified status list
 */
export const SHARED_STATUSES = {
  DRAFT: "DRAFT",
  LIVE: "LIVE",
  ARCHIVED: "ARCHIVED",
  PENDING_APPROVAL: "PENDING_APPROVAL",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  DELETED: "DELETED"
};

/**
 * Status options for map entities (CollEntity extends Record on backend)
 */
export const ENTITY_STATUSES = {
  DRAFT: "DRAFT",
  LIVE: "LIVE",
  ARCHIVED: "ARCHIVED",
  PENDING_APPROVAL: "PENDING_APPROVAL",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  DELETED: "DELETED",
};

/**
 * Status options for collection groups (CollGroup extends Record on backend).
 */
export const GROUP_STATUSES = {
  DRAFT: "DRAFT",
  LIVE: "LIVE",
  ARCHIVED: "ARCHIVED",
  PENDING_APPROVAL: "PENDING_APPROVAL",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  DELETED: "DELETED",
};

// ============================================================================
// VISIBILITY CONSTANTS
// ============================================================================

/**
 * Visibility types - controls who can see and access projects/collections
 * Used for filtering and determining access levels
 */
export const VISIBILITY_TYPES = {
  PUBLIC: "PUBLIC",         // Anyone can view (if VIEWER role has PUBLIC access)
  RESTRICTED: "RESTRICTED"  // Only explicitly invited users can access
};

// ============================================================================
// PUBLIC ACCESS LEVEL CONSTANTS
// ============================================================================

/**
 * Public Access Level types - determines the public access option
 * Used for filtering projects/collections by their public access configuration
 */
export const PUBLIC_ACCESS_LEVELS = {
  DEFAULT: "default",
  PROTECTED: "protected",
  NONE: "none",           // Legacy alias for protected
  VIEW: "view",
  EDIT: "edit",
  RESTRICTED: "restricted" // Alias for protected (backward compatibility)
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Format status names for display
 * Converts status constants to user-friendly display names
 * Example: "PENDING_APPROVAL" → "Pending Approval"
 * 
 * @param {string} status - The status constant
 * @returns {string} - Formatted status name
 */
export const formatStatusName = (status) => {
  if (!status) return '';
  // Replace underscores with spaces, lowercase all, then capitalize first letter of each word
  return status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, s => s.toUpperCase());
};

/**
 * Format visibility names for display
 * Converts visibility constants to user-friendly display names
 * Example: "RESTRICTED" → "Restricted"
 * 
 * @param {string} visibility - The visibility constant
 * @returns {string} - Formatted visibility name
 */
export const formatVisibilityName = (visibility) => {
  if (!visibility) return '';
  return visibility.charAt(0).toUpperCase() + visibility.slice(1).toLowerCase();
};

/**
 * Format public access level names for display
 * Converts public access level constants to user-friendly display names
 * Example: "edit" → "Edit", "view" → "View", "none" → "None"
 * 
 * @param {string} level - The public access level constant
 * @returns {string} - Formatted public access level name
 */
export const formatPublicAccessLevelName = (level) => {
  if (!level) return '';
  const levelMap = {
    'default': 'Default',
    'protected': 'Protected',
    'none': 'Protected',
    'view': 'View',
    'edit': 'Edit',
    'restricted': 'Protected',
    // Legacy levels for backward compatibility
    'owner': 'Edit',
    'contributor': 'Edit',
    'viewer': 'View'
  };
  return levelMap[level] || level.charAt(0).toUpperCase() + level.slice(1);
};

/**
 * Get all statuses except DELETED (for UI display)
 * 
 * @param {Object} statusConstants - PROJECT_STATUSES or COLLECTION_STATUSES
 * @returns {Array} - Array of status values excluding DELETED
 */
export const getNonDeletedStatuses = (statusConstants) => {
  return Object.values(statusConstants).filter(status => status !== 'DELETED');
};

